import React, { useState } from 'react'
import {
  ActivityIndicator,
  Platform,
  RefreshControl,
  ScrollView,
  StyleSheet,
  TouchableOpacity,
  View,
} from 'react-native'
import { useNavigation, useTheme } from '@react-navigation/native'
import type { NativeStackScreenProps } from '@react-navigation/native-stack'
import { Button, Header, Icon, Screen, Switch, Text } from '@components'
import { useLanguage } from '@context'
import type { AppNavigationProp } from '@navigation'
import { Theme } from '@theme'
import { eventPriceLabel, formatEventPrice } from '@utils'
import {
  type KtEvent,
  type OrganizerEvent,
  useEventsPullToRefresh,
  useMyEvents,
  useOrganizerEvents,
  useRefreshEventsOnFocus,
  useScreen,
} from '@hooks'
import { DiscoverEvents } from './components/DiscoverEvents'
import { OrganizerRequestBlock } from './components/OrganizerRequestBlock'
import type { EventsStackParamList } from '../../../navigation/navigator/EventsStackNavigator'

type Props = NativeStackScreenProps<EventsStackParamList, 'EventsList'>
type Tab = 'organizing' | 'attending' | 'discover'

/** "Événements": my registrations, plus my own events for organizers. */
export const EventsList: React.FC<Props> = ({ navigation }) => {
  const theme = useTheme()
  const { t } = useLanguage()
  const rootNavigation = useNavigation<AppNavigationProp>()
  const styles = getStyles(theme)
  const mine = useMyEvents()
  const isOrganizer = mine.data?.isOrganizer === true
  const organizing = useOrganizerEvents(isOrganizer)
  const { refreshing, onRefresh } = useEventsPullToRefresh()
  // A phone has room for three labels, not three labels plus icons.
  const { isMobile } = useScreen()
  const tabIcon = <T,>(icon: T) => (isMobile ? undefined : icon)
  // Back from creating / editing / an event page: the lists show the latest state.
  useRefreshEventsOnFocus()
  const [tab, setTab] = useState<Tab>('organizing')
  // Non-organizers only have two tabs; "organizing" is not one of them.
  const activeTab: Tab = !isOrganizer && tab === 'organizing' ? 'attending' : tab

  const openPublic = (code: string) => rootNavigation.navigate('PublicEvent', { code })
  const viewTicket = (eventId: string) => rootNavigation.navigate('MyTicket', { eventId })

  const list =
    activeTab === 'discover' ? (
      <DiscoverEvents onOpen={openPublic} />
    ) : activeTab === 'organizing' ? (
      (organizing.data ?? []).length === 0 ? (
        <Empty
          title={t('events.emptyOrganizing')}
          hint={t('events.emptyOrganizingHint')}
          action={
            <Button
              title={t('events.createButton')}
              iconName="plus"
              iconColor={theme.colors.white}
              onPress={() => navigation.navigate('EventCreate')}
            />
          }
        />
      ) : (
        (organizing.data ?? []).map((event) => (
          <EventRow
            key={event.id}
            event={event}
            stats={event}
            onPress={() => navigation.navigate('EventManage', { eventId: event.id })}
          />
        ))
      )
    ) : (mine.data?.attending ?? []).length === 0 ? (
      <Empty title={t('events.emptyAttending')} hint={t('events.emptyAttendingHint')} />
    ) : (
      (mine.data?.attending ?? []).map((event) => (
        <EventRow
          key={event.id}
          event={event}
          onPress={() => openPublic(event.code)}
          onViewTicket={event.phase !== 'ended' ? () => viewTicket(event.id) : undefined}
        />
      ))
    )

  return (
    <Screen>
      <Header
        title={t('events.listTitle')}
        iconLeft={Platform.OS === 'web' ? undefined : 'arrowBack'}
      />
      <ScrollView
        contentContainerStyle={styles.scroll}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={onRefresh}
            tintColor={theme.colors.primary}
          />
        }
        testID="events-list-scroll"
      >
        <View style={styles.content}>
          <View style={styles.toolbar}>
            <View style={styles.switch}>
              <Switch
                left={
                  isOrganizer
                    ? {
                        label: t('events.tabs.organizing'),
                        value: 'organizing',
                        icon: tabIcon('star' as const),
                      }
                    : {
                        label: t('events.tabs.attending'),
                        value: 'attending',
                        icon: tabIcon('calendar' as const),
                      }
                }
                middle={
                  isOrganizer
                    ? {
                        label: t('events.tabs.attending'),
                        value: 'attending',
                        icon: tabIcon('calendar' as const),
                      }
                    : undefined
                }
                right={{
                  label: t('events.tabs.discover'),
                  value: 'discover',
                  icon: tabIcon('globe' as const),
                }}
                value={activeTab}
                onChange={(value) => setTab(value as Tab)}
              />
            </View>
            {isOrganizer ? (
              <View style={styles.toolbarActions}>
                <Button
                  title={t('events.myStats.entry')}
                  iconName="croissance"
                  unfilled
                  isSmall
                  testID="open-organizer-stats"
                  onPress={() => navigation.navigate('OrganizerStats')}
                />
                <Button
                  title={t('events.createButton')}
                  iconName="plus"
                  iconColor={theme.colors.white}
                  isSmall
                  onPress={() => navigation.navigate('EventCreate')}
                />
              </View>
            ) : null}
          </View>
          {mine.data && !isOrganizer && activeTab !== 'discover' ? (
            <OrganizerRequestBlock onRequest={() => navigation.navigate('OrganizerRequest')} />
          ) : null}
          {activeTab !== 'discover' && (mine.isLoading || (isOrganizer && organizing.isLoading)) ? (
            <ActivityIndicator color={theme.colors.primary} />
          ) : (
            list
          )}
        </View>
      </ScrollView>
    </Screen>
  )
}

function Empty({ title, hint, action }: { title: string; hint: string; action?: React.ReactNode }) {
  const theme = useTheme()
  const styles = getStyles(theme)
  return (
    <View style={styles.empty}>
      <Icon name="calendarStar" size={48} color={theme.colors.primary} />
      <Text type="subtitleBold" center>
        {title}
      </Text>
      <Text type="body" center color={theme.colors.greyBlue}>
        {hint}
      </Text>
      {action ? <View style={styles.emptyAction}>{action}</View> : null}
    </View>
  )
}

function EventRow({
  event,
  stats,
  onPress,
  onViewTicket,
}: {
  event: KtEvent
  stats?: OrganizerEvent
  onPress: () => void
  onViewTicket?: () => void
}) {
  const theme = useTheme()
  const { t, currentLanguage } = useLanguage()
  const styles = getStyles(theme)
  const start = new Date(event.startsAt)
  const locale = currentLanguage === 'en' ? 'en-GB' : currentLanguage === 'es' ? 'es-ES' : 'fr-FR'
  const month = new Intl.DateTimeFormat(locale, { month: 'short' }).format(start).replace('.', '')
  const time = new Intl.DateTimeFormat(locale, {
    weekday: 'long',
    hour: '2-digit',
    minute: '2-digit',
  }).format(start)
  const presentLabel = stats ? `${t('events.manage.stats.present')} ${stats.stats.checkedIn}` : ''
  const phaseColor =
    event.phase === 'ongoing'
      ? theme.colors.green
      : event.phase === 'ended'
        ? theme.colors.greyBlue
        : theme.colors.primary

  return (
    <View style={styles.row}>
      <TouchableOpacity accessibilityRole="button" onPress={onPress} style={styles.rowMain}>
        <View style={[styles.dateBlock, event.phase === 'ended' && styles.dateBlockEnded]}>
          <Text type="bodyExtraSmallBold" color={theme.colors.white}>
            {month.toUpperCase()}
          </Text>
          <Text type="titleBigBold" color={theme.colors.white}>
            {String(start.getDate())}
          </Text>
        </View>
        <View style={styles.rowBody}>
          <Text type="subtitleBold" numberOfLines={1}>
            {event.name}
          </Text>
          <Text type="bodySmall" color={theme.colors.greyBlue} numberOfLines={1}>
            {[time, event.location].filter(Boolean).join(' · ')}
          </Text>
          <View style={styles.chips}>
            <View style={[styles.chip, { borderColor: phaseColor }]}>
              <Text type="bodyExtraSmallBold" color={phaseColor}>
                {t(`events.phase.${event.phase}`)}
              </Text>
            </View>
            <View style={styles.chip}>
              <Text type="bodyExtraSmallBold" color={theme.colors.secondary}>
                {event.capacity !== null
                  ? t('events.attendeesCountWithCapacity', {
                      count: event.attendeeCount,
                      capacity: event.capacity,
                    })
                  : t('events.attendeesCount', { count: event.attendeeCount })}
              </Text>
            </View>
            {stats ? (
              <View style={styles.chip}>
                <Text type="bodyExtraSmallBold" color={theme.colors.green}>
                  {presentLabel}
                </Text>
              </View>
            ) : null}
            {event.priceCents !== null ? (
              <View style={styles.chip} testID={`event-row-price-${event.id}`}>
                <Text type="bodyExtraSmallBold" color={theme.colors.primary}>
                  {stats
                    ? t('events.manage.stats.collectedShort', {
                        amount: formatEventPrice(
                          stats.stats.collectedCents,
                          currentLanguage,
                          event.currency,
                        ),
                        price: eventPriceLabel(event, '', currentLanguage),
                      })
                    : eventPriceLabel(event, '', currentLanguage)}
                </Text>
              </View>
            ) : null}
          </View>
        </View>
        <Icon name="arrowRight" size={18} color={theme.colors.greyBlue} />
      </TouchableOpacity>
      {onViewTicket ? (
        <TouchableOpacity
          accessibilityRole="button"
          testID={`view-ticket-${event.id}`}
          onPress={onViewTicket}
          style={styles.ticketButton}
        >
          <Icon name="qrCode" size={22} color={theme.colors.primary} />
        </TouchableOpacity>
      ) : null}
    </View>
  )
}

const getStyles = (theme: Theme) =>
  StyleSheet.create({
    scroll: { padding: theme.spacing.xl, alignItems: 'center' },
    content: { width: '100%', maxWidth: 880, gap: theme.spacing.md },
    toolbar: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      flexWrap: 'wrap',
      gap: theme.spacing.md,
      marginBottom: theme.spacing.sm,
    },
    // Grows to the full row on a phone (the create button then wraps below).
    switch: { minWidth: 280, flexShrink: 1, flexGrow: 1 },
    // Wraps too: with a large system font on a 360 dp phone the two buttons
    // no longer fit side by side ("Créer un événement" went off-screen).
    toolbarActions: { flexDirection: 'row', flexWrap: 'wrap', maxWidth: '100%', gap: theme.spacing.sm },
    row: {
      flexDirection: 'row',
      alignItems: 'center',
      padding: theme.spacing.lg,
      borderRadius: theme.border.large,
      backgroundColor: theme.colors.white,
      borderWidth: 1,
      borderColor: theme.colors.grey,
    },
    rowMain: { flex: 1, flexDirection: 'row', alignItems: 'center', gap: theme.spacing.lg, minWidth: 0 },
    ticketButton: { padding: theme.spacing.sm, marginLeft: theme.spacing.xs },
    dateBlock: {
      width: 64,
      height: 68,
      borderRadius: theme.border.medium,
      backgroundColor: theme.colors.primary,
      alignItems: 'center',
      justifyContent: 'center',
    },
    dateBlockEnded: { backgroundColor: theme.colors.greyBlue },
    rowBody: { flex: 1, minWidth: 0, gap: 2 },
    chips: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: theme.spacing.xs,
      marginTop: theme.spacing.xs,
    },
    chip: {
      borderWidth: 1,
      borderColor: theme.colors.grey,
      borderRadius: theme.border.jumbo,
      paddingHorizontal: theme.spacing.sm,
      paddingVertical: 2,
    },
    empty: { alignItems: 'center', gap: theme.spacing.sm, paddingVertical: theme.spacing.xxxl * 2 },
    emptyAction: { marginTop: theme.spacing.lg },
  })
